import { Component } from '@theme/component';
import { lockScroll, unlockScroll } from '@theme/utilities';

/**
 * Guaranteed welcome offer: flip or scratch, then sign up, then see the code.
 * Shopify validates signup; a confirmed response reveals the code without reloading.
 * @extends {Component<{panel: HTMLDialogElement, launcher: HTMLButtonElement, launcherLabel: HTMLElement, title: HTMLElement, game: HTMLElement, gameDescription: HTMLElement, modes: HTMLElement, flipMode: HTMLButtonElement, scratchMode: HTMLButtonElement, flipArea: HTMLElement, flipCard: HTMLButtonElement, flipFront: HTMLElement, flipBack: HTMLElement, scratchArea: HTMLElement, canvas: HTMLCanvasElement, scratchFallback: HTMLButtonElement, claim: HTMLButtonElement, email: HTMLInputElement, signup: HTMLElement, success: HTMLElement, successHeading: HTMLElement, result: HTMLElement, code: HTMLElement, copyStatus: HTMLElement, signupSubmit: HTMLButtonElement, signupLabel: HTMLElement, signupStatus: HTMLElement, signupError: HTMLElement, signupFallback: HTMLButtonElement}>}
 */
class DiscountPopup extends Component {
  #timer;
  #events;
  #resize;
  #previousFocus;
  #revealed = false;
  #mode = 'flip';
  #context;
  #pointer;
  #lastPoint;
  #canvasWidth = 0;
  #canvasHeight = 0;
  #signupTimer;
  #preparingSignup = false;
  #standardSignup = false;
  #signupRequest;
  #signupForm;

  connectedCallback() { super.connectedCallback(); this.#initialize(); }
  updatedCallback() { super.updatedCallback(); this.#initialize(); }
  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#events?.abort();
    this.#resize?.disconnect();
    clearTimeout(this.#signupTimer);
    this.#signupRequest?.abort();
    unlockScroll(this.refs.panel);
    super.disconnectedCallback();
  }

  #initialize() {
    clearTimeout(this.#timer);
    this.#events?.abort();
    this.#resize?.disconnect();
    this.#events = new AbortController();
    clearTimeout(this.#signupTimer);
    this.#preparingSignup = false;
    this.#standardSignup = false;
    this.#signupRequest?.abort();
    this.#signupRequest = undefined;
    this.#canvasWidth = 0;
    this.#canvasHeight = 0;
    this.#context = undefined;
    this.#pointer = undefined;
    const { signal } = this.#events;
    const state = this.refs.result.dataset.state;
    this.refs.launcher.hidden = false;
    this.#revealed = Boolean(this.#read('sessionStorage', this.#revealedKey));
    this.#setMode('flip');
    this.refs.panel.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.closePopup();
    }, { signal });
    this.refs.panel.addEventListener('close', () => unlockScroll(this.refs.panel), { signal });
    document.addEventListener('keydown', this.#handlePanelKeydown, { signal });
    document.addEventListener('focusin', this.#keepPanelFocus, { signal });
    const form = this.querySelector('.nt-discount-popup__form');
    // Shopify automatically protects this form when enabled in store preferences.
    // Let that integration capture our transport on interaction; calling protect()
    // explicitly would force CAPTCHA even after the merchant switches it off.
    if (this.#signupForm !== form) {
      this.#signupForm = form;
      if (form.submit === HTMLFormElement.prototype.submit) {
        form.submit = () => this.#sendSignup(form);
      }
    }
    this.addEventListener('submit', (event) => {
      if (event.target !== form || event.defaultPrevented || this.#standardSignup) return;
      event.preventDefault();
      this.#sendSignup(form);
    }, { signal });
    const canvas = this.refs.canvas;
    canvas.addEventListener('pointerdown', this.#scratchStart, { signal });
    canvas.addEventListener('pointermove', this.#scratchMove, { signal });
    canvas.addEventListener('pointerup', this.#scratchEnd, { signal });
    canvas.addEventListener('pointercancel', this.#scratchEnd, { signal });
    this.#resize = new ResizeObserver(() => this.#paintScratch());
    this.#resize.observe(canvas);

    if (state === 'success' || this.#read('localStorage', this.#unlockedKey)) {
      this.#showCode();
    } else if (state === 'error') {
      this.#revealed = true;
      this.#showSignup();
    } else {
      this.refs.game.hidden = false;
      this.refs.gameDescription.hidden = false;
      this.refs.signup.hidden = true;
      this.refs.success.hidden = true;
      this.#updateReward();
    }
    if (state === 'success') {
      this.#write('localStorage', this.#unlockedKey, '1');
      this.openPopup();
      return;
    }
    if (state === 'error') { this.openPopup(); return; }
    if (window.Shopify?.designMode) {
      const section = this.closest('.shopify-section');
      section?.addEventListener('shopify:section:select', () => this.openPopup(), { signal });
      section?.addEventListener('shopify:section:deselect', () => this.closePopup(), { signal });
      return;
    }
    if (this.#read('localStorage', this.#unlockedKey) || this.#read('sessionStorage', this.#shownKey)) return;
    const delay = Number(this.dataset.delay);
    this.#timer = setTimeout(this.#tryOpen, (Number.isFinite(delay) ? Math.max(5, Math.min(60, delay)) : 5) * 1000);
  }

  #tryOpen = () => {
    if (!this.isConnected || this.refs.panel.open || this.#read('sessionStorage', this.#shownKey)) return;
    const active = document.activeElement;
    const typing = active instanceof HTMLElement && (active.matches('input, textarea, select') || active.isContentEditable);
    if (document.hidden || document.querySelector('dialog[open], details[open][scroll-lock]') || typing) {
      this.#timer = setTimeout(this.#tryOpen, 1000);
      return;
    }
    this.openPopup();
  };

  openPopup(event) {
    event?.preventDefault();
    clearTimeout(this.#timer);
    if (this.refs.panel.open) return;
    this.#previousFocus = document.activeElement;
    // Shopify mounts its hCaptcha challenge outside this dialog. A native modal
    // makes that iframe inert and puts it behind the browser's top layer.
    this.refs.panel.show();
    lockScroll(this.refs.panel);
    this.#rememberShown();
    const target = !this.refs.success.hidden ? this.refs.successHeading : !this.refs.signup.hidden ? this.refs.email : this.refs.title;
    target.focus({ preventScroll: true });
    this.#paintScratch();
  }

  closePopup() {
    clearTimeout(this.#timer);
    this.refs.panel.close();
    unlockScroll(this.refs.panel);
    this.#rememberShown();
    const target = this.#previousFocus instanceof HTMLElement && this.#previousFocus.isConnected && this.#previousFocus !== document.body
      ? this.#previousFocus : this.refs.launcher;
    target.focus({ preventScroll: true });
  }

  #captchaFrames() {
    return Array.from(document.querySelectorAll('iframe[src*="hcaptcha.com"]'));
  }

  #captchaIsVisible() {
    return this.#captchaFrames().some((frame) => {
      const rect = frame.getBoundingClientRect();
      return rect.width > 100 && rect.height > 100 && rect.bottom > 0 && rect.right > 0
        && rect.top < window.innerHeight && rect.left < window.innerWidth
        && getComputedStyle(frame).visibility !== 'hidden';
    });
  }

  // Keep keyboard navigation in the offer, allowing Shopify's separate CAPTCHA
  // iframe to receive focus and handle its own keyboard controls.
  #keepPanelFocus = (event) => {
    const { panel } = this.refs;
    if (!panel.open || panel.contains(event.target) || this.#captchaFrames().includes(document.activeElement)) return;
    const target = !this.refs.success.hidden ? this.refs.successHeading
      : !this.refs.signup.hidden ? this.refs.email : this.refs.title;
    target.focus({ preventScroll: true });
  };

  #handlePanelKeydown = (event) => {
    const { panel } = this.refs;
    if (!panel.open || this.#captchaIsVisible()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closePopup();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = Array.from(panel.querySelectorAll(
      'a[href], button:enabled, input:not([type="hidden"]):enabled, [tabindex]:not([tabindex="-1"])'
    )).filter((element) => element.getClientRects().length > 0);
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  chooseFlip() { this.#setMode('flip'); }
  chooseScratch() { this.#setMode('scratch'); }
  #setMode(mode) {
    this.#mode = mode;
    this.refs.flipArea.hidden = mode !== 'flip';
    this.refs.scratchArea.hidden = mode !== 'scratch';
    this.refs.flipMode.setAttribute('aria-pressed', String(mode === 'flip'));
    this.refs.scratchMode.setAttribute('aria-pressed', String(mode === 'scratch'));
    if (mode === 'scratch') this.#paintScratch();
  }

  revealOffer() {
    if (this.#revealed) return;
    this.#revealed = true;
    this.#write('sessionStorage', this.#revealedKey, '1');
    this.#updateReward();
    this.refs.claim.focus({ preventScroll: true });
  }
  #updateReward() {
    this.refs.flipCard.classList.toggle('is-flipped', this.#revealed);
    this.refs.flipFront.setAttribute('aria-hidden', String(this.#revealed));
    this.refs.flipBack.setAttribute('aria-hidden', String(!this.#revealed));
    this.refs.flipCard.setAttribute('aria-label', this.#revealed ? this.dataset.reward : this.dataset.flipLabel);
    this.refs.canvas.hidden = this.#revealed;
    this.refs.scratchFallback.hidden = this.#revealed;
    this.refs.claim.hidden = !this.#revealed;
    this.refs.modes.hidden = this.#revealed;
  }
  claimOffer() {
    if (!this.#revealed) return;
    this.#showSignup();
    this.refs.email.focus({ preventScroll: true });
  }
  #showSignup() {
    this.refs.game.hidden = true;
    this.refs.gameDescription.hidden = true;
    this.refs.signup.hidden = false;
    this.refs.success.hidden = true;
    this.refs.email.disabled = false;
  }
  backToCard() {
    this.refs.game.hidden = false;
    this.refs.gameDescription.hidden = false;
    this.refs.signup.hidden = true;
    this.#updateReward();
    this.refs.claim.focus({ preventScroll: true });
  }

  #paintScratch() {
    if (this.#revealed || this.#mode !== 'scratch') return;
    const canvas = this.refs.canvas;
    const { width, height } = canvas.getBoundingClientRect();
    if (!width || !height || (width === this.#canvasWidth && height === this.#canvasHeight)) return;
    this.#canvasWidth = width;
    this.#canvasHeight = height;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return; // The adjacent reveal button is always available.
    this.#context = context;
    context.scale(ratio, ratio);
    context.globalCompositeOperation = 'source-over';
    const gold = context.createLinearGradient(0, 0, width, height);
    gold.addColorStop(0, '#f7e7aa'); gold.addColorStop(0.42, '#e7c878');
    gold.addColorStop(0.68, '#fae9b4'); gold.addColorStop(1, '#c49b4e');
    context.fillStyle = gold;
    context.fillRect(0, 0, width, height);
    context.strokeStyle = '#fff7d1'; context.lineWidth = 1;
    context.strokeRect(12, 12, width - 24, height - 24);
    context.fillStyle = '#183f30'; context.textAlign = 'center'; context.textBaseline = 'middle';
    context.font = `${Math.min(30, width / 10)}px Georgia, serif`;
    context.fillText(this.dataset.scratchLabel, width / 2, height / 2);
    context.globalCompositeOperation = 'destination-out';
    context.lineWidth = 42; context.lineCap = 'round'; context.lineJoin = 'round';
  }
  #point(event) {
    const rect = this.refs.canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }
  #scratchStart = (event) => {
    if (this.#revealed || !this.#context || !event.isPrimary || event.button !== 0) return;
    event.preventDefault();
    this.#pointer = event.pointerId;
    this.refs.canvas.setPointerCapture(event.pointerId);
    this.#lastPoint = this.#point(event);
    this.#erase(this.#lastPoint);
  };
  #scratchMove = (event) => {
    if (event.pointerId !== this.#pointer || !this.#context) return;
    event.preventDefault();
    const point = this.#point(event);
    this.#erase(point);
    this.#lastPoint = point;
  };
  #erase(point) {
    this.#context.beginPath();
    this.#context.moveTo(this.#lastPoint.x, this.#lastPoint.y);
    this.#context.lineTo(point.x + 0.1, point.y + 0.1);
    this.#context.stroke();
  }
  #scratchEnd = (event) => {
    if (event.pointerId !== this.#pointer) return;
    this.#pointer = undefined;
    if (this.refs.canvas.hasPointerCapture(event.pointerId)) this.refs.canvas.releasePointerCapture(event.pointerId);
    if (!this.#context) return;
    const { width, height } = this.refs.canvas;
    const pixels = this.#context.getImageData(0, 0, width, height).data;
    let clear = 0, samples = 0;
    for (let y = 0; y < height; y += 12) {
      for (let x = 0; x < width; x += 12) {
        samples++;
        if (pixels[(y * width + x) * 4 + 3] < 128) clear++;
      }
    }
    if (samples && clear / samples >= 0.25) this.revealOffer();
  };

  /** Immediate feedback also covers Shopify's invisible CAPTCHA preparation. */
  prepareSignup(event) {
    const form = this.querySelector('.nt-discount-popup__form');
    if (!(form instanceof HTMLFormElement) || !form.checkValidity()) return;
    if (this.#preparingSignup) { event.preventDefault(); return; }
    this.#preparingSignup = true;
    this.refs.signupError.hidden = true;
    this.refs.signupFallback.hidden = true;
    this.#signupBusy(true);
    clearTimeout(this.#signupTimer);
    const checkPreparation = () => {
      // Give visitors as long as they need to complete a visible challenge.
      if (this.isConnected && this.#captchaIsVisible()) {
        this.#signupTimer = setTimeout(checkPreparation, 1000);
        return;
      }
      if (this.isConnected) {
        this.#preparingSignup = false;
        this.#signupBusy(false);
        this.refs.signupStatus.textContent = this.dataset.signupSlow;
        this.refs.signupFallback.hidden = false;
      }
    };
    this.#signupTimer = setTimeout(checkPreparation, 15000);
  }

  async #sendSignup(form) {
    if (this.#standardSignup) { HTMLFormElement.prototype.submit.call(form); return; }
    if (this.#signupRequest || !form.reportValidity()) return;
    const request = new AbortController();
    this.#signupRequest = request;
    this.#preparingSignup = true;
    clearTimeout(this.#signupTimer);
    this.#signupBusy(true);
    const timeout = setTimeout(() => request.abort(), 20000);
    try {
      // Keep the DOM form's return_to on a complete storefront page. Shopify can
      // submit natively after verification, independently of this fetch transport.
      const body = new URLSearchParams(new FormData(form));
      body.set('return_to', this.dataset.signupReturn);
      const response = await fetch(form.action, {
        method: 'POST', body,
        credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: request.signal,
      });
      const destination = new URL(response.url);
      if (destination.origin === location.origin && /(?:^|\/)challenge(?:\/|$)/.test(destination.pathname)) {
        location.assign(destination.href);
        return;
      }
      const html = await response.text();
      if (!this.isConnected || request !== this.#signupRequest) return;
      const returnedDocument = new DOMParser().parseFromString(html, 'text/html');
      const result = returnedDocument.querySelector('[data-discount-signup-result]');
      const returnedForm = returnedDocument.getElementById(form.id);
      const state = result?.getAttribute('data-state') || returnedForm?.querySelector('[ref="result"]')?.getAttribute('data-state');
      if (response.ok && state === 'success') {
        this.#write('localStorage', this.#unlockedKey, '1');
        this.refs.result.dataset.state = 'success';
        this.#showCode();
        if (this.refs.panel.open) this.refs.successHeading.focus({ preventScroll: true });
        else this.openPopup();
      } else {
        const error = returnedDocument.querySelector('[data-discount-signup-error]')?.textContent.trim() || returnedForm?.querySelector('[ref="signupError"]')?.textContent.trim();
        this.#signupFailed(error || this.dataset.signupError);
      }
    } catch {
      if (this.isConnected && request === this.#signupRequest) this.#signupFailed(this.dataset.signupError);
    } finally {
      clearTimeout(timeout);
      if (request === this.#signupRequest) {
        this.#signupRequest = undefined;
        this.#preparingSignup = false;
        this.#signupBusy(false);
      }
    }
  }

  #signupBusy(busy) {
    this.refs.signupSubmit.setAttribute('aria-busy', String(busy));
    this.refs.signupLabel.textContent = busy ? this.dataset.signupLoading : this.dataset.signupLabel;
    this.refs.signupStatus.textContent = busy ? this.dataset.signupLoading : '';
  }

  #signupFailed(message) {
    this.refs.signupError.textContent = message;
    this.refs.signupError.hidden = false;
    this.refs.signupFallback.hidden = false;
  }

  /** Keep Shopify's standard protected form available if enhancement fails. */
  submitStandardSignup() {
    this.#standardSignup = true;
    this.#preparingSignup = false;
    const form = this.querySelector('.nt-discount-popup__form');
    form.elements.return_to.value = this.dataset.standardReturn;
    this.prepareSignup({ preventDefault() {} });
    form.requestSubmit();
  }

  async copyCode() {
    try {
      await navigator.clipboard.writeText(this.refs.code.textContent.trim());
      if (this.isConnected) this.refs.copyStatus.textContent = this.dataset.copySuccess;
    } catch {
      const selection = window.getSelection(), range = document.createRange();
      range.selectNodeContents(this.refs.code);
      selection?.removeAllRanges(); selection?.addRange(range);
      this.refs.copyStatus.textContent = this.dataset.copyFallback;
    }
  }
  #showCode() {
    this.refs.game.hidden = true;
    this.refs.gameDescription.hidden = true;
    this.refs.signup.hidden = true;
    this.refs.email.disabled = true;
    this.refs.success.hidden = false;
    this.refs.launcherLabel.textContent = this.dataset.unlockedLabel;
  }
  #rememberShown() {
    if (!window.Shopify?.designMode) this.#write('sessionStorage', this.#shownKey, '1');
  }
  // Only offer progress is stored, never the visitor's email address.
  #read(storage, key) { try { return window[storage].getItem(key); } catch { return null; } }
  #write(storage, key, value) { try { window[storage].setItem(key, value); } catch { /* The offer works without storage. */ } }
  get #unlockedKey() { return `nataura:email-discount:${this.dataset.code}`; }
  get #shownKey() { return `${this.#unlockedKey}:cards-v1:shown`; }
  get #revealedKey() { return `${this.#unlockedKey}:cards-v1:revealed`; }
}

if (!customElements.get('nt-discount-popup-component')) customElements.define('nt-discount-popup-component', DiscountPopup);
